/**
 * useTeams — the React binding for the Teams feature.
 *
 * Strict layering: pages call THIS hook; the hook calls {@link TeamStore}; the
 * store calls the api facade. Pages never import the store or the api directly.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { TeamStore } from './teamStore';
import { buildPeopleDirectory, sortTeamsByRecency } from './teamModel';
import type { DirectoryPerson, Team, TeamDraft, TeamSummary } from './types';

export interface UseTeamsResult {
  teams: TeamSummary[];
  /** Deduped people directory aggregated across all teams (feeds pickers). */
  people: DirectoryPerson[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  getById: (id: string) => Promise<Team | undefined>;
  create: (draft: TeamDraft) => Promise<Team>;
  update: (id: string, draft: TeamDraft) => Promise<Team | undefined>;
  remove: (id: string) => Promise<void>;
}

export function useTeams(): UseTeamsResult {
  const api = useApi();
  const store = useMemo(() => new TeamStore(api), [api]);

  const [teams, setTeams] = useState<TeamSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await store.listSummaries();
      setTeams(sortTeamsByRecency(rows));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load teams.');
    } finally {
      setLoading(false);
    }
  }, [store]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const people = useMemo(() => buildPeopleDirectory(teams), [teams]);

  const getById = useCallback((id: string) => store.get(id), [store]);

  const create = useCallback(
    async (draft: TeamDraft) => {
      const saved = await store.create(draft);
      await refresh();
      return saved;
    },
    [store, refresh],
  );

  const update = useCallback(
    async (id: string, draft: TeamDraft) => {
      const existing = await store.get(id);
      if (!existing) return undefined;
      const saved = await store.applyDraft(existing, draft);
      await refresh();
      return saved;
    },
    [store, refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await store.softDelete(id);
      await refresh();
    },
    [store, refresh],
  );

  return {
    teams,
    people,
    loading,
    error,
    refresh,
    getById,
    create,
    update,
    remove,
  };
}
